"use client";

import { useEffect, useId, useMemo, useState } from "react";

import { AlertCircleIcon, InfoIcon, TruckIcon } from "lucide-react";

import { Alert, AlertDescription } from "@/components/ui/alert";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldGroup,
  FieldLabel,
} from "@/components/ui/field";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import type { BodyworkData } from "@/lib/schemas/vehicle-assignment";
import { useBodyTypesQuery } from "@/lib/queries/body-types";
import { useComboboxMultiFieldFilter } from "@/hooks/use-combobox-multifield-filter";

// TEMP(vehicle-assignment): mock body shops until body shop API exists.
const MOCK_BODY_SHOPS = [
  { kvps: "FRA59130N", label: "Lambersart Carrosserie", postalCode: "59130" },
  { kvps: "FRA75001N", label: "Paris Carrosserie", postalCode: "75001" },
  { kvps: "FRA69001N", label: "Lyon Utilitaires", postalCode: "69001" },
  { kvps: "FRA13001N", label: "Marseille BV", postalCode: "13001" },
  { kvps: "FRA33000N", label: "Bordeaux Plateaux", postalCode: "33000" },
  { kvps: "FRA06000N", label: "Nice Benne", postalCode: "06000" },
  { kvps: "FRA31000N", label: "Toulouse Fourgon", postalCode: "31000" },
  { kvps: "FRA44000N", label: "Nantes Cellules", postalCode: "44000" },
  { kvps: "FRA67000N", label: "Strasbourg Nord", postalCode: "67000" },
  { kvps: "FRA35000N", label: "Rennes Ouest", postalCode: "35000" },
] as const;

type BodyShopMockItem = (typeof MOCK_BODY_SHOPS)[number];

type NeedsBodyworkAnswer = "" | "no" | "yes";

type ShippedAnswer = "" | "no" | "yes";

function RetailVehicleBodyStep({
  onCanProceedChange,
  onChangeBodyworkData,
}: {
  onCanProceedChange?: (canProceed: boolean) => void;
  onChangeBodyworkData?: (data: BodyworkData) => void;
}) {
  const baseId = useId();
  const bodyTypesQuery = useBodyTypesQuery({ sort: { name: "asc" } });

  const [needsBodyworkAnswer, setNeedsBodyworkAnswer] =
    useState<NeedsBodyworkAnswer>("");
  const [bodyTypeId, setBodyTypeId] = useState<string>("");
  const [bodyShopPostalCode, setBodyShopPostalCode] = useState<string>("");
  const [bodyShopKvps, setBodyShopKvps] = useState<string>("");
  const [shippedAnswer, setShippedAnswer] = useState<ShippedAnswer>("");

  const activeBodyTypes = useMemo(() => {
    const list = bodyTypesQuery.data?.bodyTypes ?? [];
    return list.filter((b) => b.isActive);
  }, [bodyTypesQuery.data?.bodyTypes]);

  const bodyTypesSelectable =
    bodyTypesQuery.isSuccess &&
    !bodyTypesQuery.isError &&
    activeBodyTypes.length > 0;

  const showBodyworkFields = needsBodyworkAnswer === "yes";
  const showBlockingBodyTypeError =
    showBodyworkFields &&
    !bodyTypesQuery.isPending &&
    (bodyTypesQuery.isError || !bodyTypesSelectable);

  const canProceed = useMemo(() => {
    if (needsBodyworkAnswer === "" || needsBodyworkAnswer === "no") {
      if (needsBodyworkAnswer === "no") return true;
      return false;
    }
    if (needsBodyworkAnswer === "yes") {
      if (bodyTypesQuery.isPending) return false;
      if (!bodyTypesSelectable) return false;
      if (!bodyTypeId) return false;
      if (!bodyShopKvps) return false;
      if (shippedAnswer !== "yes" && shippedAnswer !== "no") return false;
      return true;
    }
    return false;
  }, [
    bodyTypeId,
    bodyTypesQuery.isPending,
    bodyTypesSelectable,
    bodyShopKvps,
    needsBodyworkAnswer,
    shippedAnswer,
  ]);

  const bodyworkData = useMemo<BodyworkData>(() => {
    const parsedBodyTypeId = bodyTypeId
      ? Number.parseInt(bodyTypeId, 10)
      : null;
    return {
      bodyTypeId:
        parsedBodyTypeId !== null && Number.isFinite(parsedBodyTypeId)
          ? parsedBodyTypeId
          : null,
      bodyShopKvps: bodyShopKvps || null,
      bodyShopPostalCode,
      needsBodywork:
        needsBodyworkAnswer === "yes"
          ? true
          : needsBodyworkAnswer === "no"
            ? false
            : null,
      shippedToBodyShop:
        shippedAnswer === "yes" ? true : shippedAnswer === "no" ? false : null,
    };
  }, [
    bodyTypeId,
    bodyShopKvps,
    bodyShopPostalCode,
    needsBodyworkAnswer,
    shippedAnswer,
  ]);

  useEffect(() => {
    onCanProceedChange?.(canProceed);
  }, [canProceed, onCanProceedChange]);

  useEffect(() => {
    onChangeBodyworkData?.(bodyworkData);
  }, [bodyworkData, onChangeBodyworkData]);

  const bodyShopFilter = useComboboxMultiFieldFilter<BodyShopMockItem>({
    textSelectors: [(i) => i.label],
    digitSelectors: [(i) => i.postalCode],
  });

  const selectedBodyShop = useMemo<BodyShopMockItem | null>(() => {
    return (
      (MOCK_BODY_SHOPS as unknown as BodyShopMockItem[]).find(
        (c) => c.kvps === bodyShopKvps,
      ) ?? null
    );
  }, [bodyShopKvps]);

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <TruckIcon className="text-primary size-5" />
          Véhicule Carrossé
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <Alert>
          <InfoIcon />
          <AlertDescription>
            Cette étape concerne les véhicules utilitaires nécessitant un
            carrossage spécifique (benne, plateau, fourgon aménagé, etc.).
          </AlertDescription>
        </Alert>

        <FieldGroup className="gap-6">
          <Field>
            <FieldLabel>
              Ce véhicule nécessite-t-il un carrossage&nbsp;?
            </FieldLabel>
            <FieldContent>
              <RadioGroup
                className="flex flex-wrap gap-4"
                value={needsBodyworkAnswer}
                onValueChange={(v) => {
                  const next = v as NeedsBodyworkAnswer;
                  setNeedsBodyworkAnswer(next);
                  if (next === "no") {
                    setBodyTypeId("");
                    setBodyShopKvps("");
                    setShippedAnswer("");
                  }
                }}
              >
                <div className="flex items-center gap-2">
                  <RadioGroupItem id={`${baseId}-nw-yes`} value="yes" />
                  <Label htmlFor={`${baseId}-nw-yes`}>Oui</Label>
                </div>
                <div className="flex items-center gap-2">
                  <RadioGroupItem id={`${baseId}-nw-no`} value="no" />
                  <Label htmlFor={`${baseId}-nw-no`}>Non</Label>
                </div>
              </RadioGroup>
            </FieldContent>
          </Field>

          {showBodyworkFields ? (
            <>
              {bodyTypesQuery.isPending ? (
                <div className="space-y-2">
                  <Skeleton className="h-4 w-48" />
                  <Skeleton className="h-9 w-full max-w-sm" />
                </div>
              ) : null}

              {showBlockingBodyTypeError ? (
                <Alert variant="destructive">
                  <AlertCircleIcon />
                  <AlertDescription>
                    Impossible de charger les types de carrosserie. Vérifiez
                    votre connexion ou réessayez plus tard. Si le problème
                    persiste, contactez le support.
                  </AlertDescription>
                </Alert>
              ) : null}

              {showBodyworkFields &&
              bodyTypesQuery.isSuccess &&
              bodyTypesSelectable ? (
                <Field>
                  <FieldLabel>Type de carrosserie</FieldLabel>
                  <FieldContent>
                    <Select
                      value={bodyTypeId || undefined}
                      onValueChange={setBodyTypeId}
                    >
                      <SelectTrigger className="w-full max-w-sm">
                        <SelectValue placeholder="Sélectionner…" />
                      </SelectTrigger>
                      <SelectContent>
                        {activeBodyTypes.map((bt) => (
                          <SelectItem key={bt.id} value={String(bt.id)}>
                            {bt.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </FieldContent>
                </Field>
              ) : null}

              {showBodyworkFields && bodyTypesSelectable ? (
                <>
                  <div className="w-full max-w-sm">
                    <Field>
                      <FieldLabel>Carrossier</FieldLabel>
                      <FieldContent>
                        <Combobox
                          items={
                            MOCK_BODY_SHOPS as unknown as BodyShopMockItem[]
                          }
                          itemToStringLabel={(c: BodyShopMockItem) => c.label}
                          filter={bodyShopFilter}
                          value={selectedBodyShop}
                          onValueChange={(next: BodyShopMockItem | null) => {
                            if (!next) {
                              setBodyShopKvps("");
                              setBodyShopPostalCode("");
                              return;
                            }

                            setBodyShopKvps(next.kvps);
                            setBodyShopPostalCode(next.postalCode);
                          }}
                        >
                          <ComboboxInput
                            placeholder="Rechercher…"
                            showClear={bodyShopKvps !== ""}
                            aria-label="Rechercher un carrossier"
                          />
                          <ComboboxContent>
                            <ComboboxEmpty>
                              Aucun carrossier trouvé.
                            </ComboboxEmpty>
                            <ComboboxList>
                              {(c: BodyShopMockItem) => (
                                <ComboboxItem key={c.kvps} value={c}>
                                  {c.label}
                                </ComboboxItem>
                              )}
                            </ComboboxList>
                          </ComboboxContent>
                        </Combobox>
                        <FieldDescription>
                          Vous pouvez filtrer en tapant le nom du carrossier ou
                          son code postal (ex. 75001).
                        </FieldDescription>
                      </FieldContent>
                    </Field>
                  </div>

                  <Field>
                    <FieldLabel>
                      Véhicule expédié chez le carrossier&nbsp;?
                    </FieldLabel>
                    <FieldContent>
                      <RadioGroup
                        className="flex flex-wrap gap-4"
                        value={shippedAnswer}
                        onValueChange={(v) =>
                          setShippedAnswer(v as ShippedAnswer)
                        }
                      >
                        <div className="flex items-center gap-2">
                          <RadioGroupItem id={`${baseId}-sh-yes`} value="yes" />
                          <Label htmlFor={`${baseId}-sh-yes`}>Oui</Label>
                        </div>
                        <div className="flex items-center gap-2">
                          <RadioGroupItem id={`${baseId}-sh-no`} value="no" />
                          <Label htmlFor={`${baseId}-sh-no`}>Non</Label>
                        </div>
                      </RadioGroup>
                    </FieldContent>
                  </Field>
                </>
              ) : null}
            </>
          ) : null}
        </FieldGroup>
      </CardContent>
    </Card>
  );
}

export { RetailVehicleBodyStep };
